import type { ProviderId } from '@/types/providers';
import type { AIProvider } from './provider-interface';

/**
 * Provider capabilities configuration
 */
export interface ProviderCapabilities {
  // Core capabilities
  text: boolean;
  image: boolean;

  // Image-specific capabilities
  imageEdit: boolean;
  imageEditIdentityConsistent: boolean;

  // Document capabilities
  documentSupport: boolean;
  nativePDF: boolean;

  // Additional capabilities
  internetSearch?: boolean;
}

/**
 * Provider validation configuration
 */
export interface ProviderValidation {
  keyFormat: string;
  keyPrefix?: string;
  keyLength?: { min: number; max?: number };
  getKeyUrl: string;
  docsUrl?: string;
}

/**
 * Provider display information
 */
export interface ProviderDisplay {
  description: string;
  notice?: string;
  recommendations?: string[];
}

/**
 * Provider document support configuration
 */
export interface ProviderDocumentSupport {
  supportsNativePDF: boolean;
  maxPDFSize: number; // in bytes
  supportedFormats: string[];
  notes?: string;
}

/**
 * Provider image generation details
 */
export interface ProviderImageDetails {
  maxResolution?: string;
  maxImagesPerMinute?: number;
  costPerImage?: string;
  qualityOptions?: string[];
  sizeOptions?: string[];
}

/**
 * Image model configuration for a provider
 */
export interface ProviderImageModels {
  supportsEdit?: string[];
  supportsIdentityConsistentEdit?: string[];
}

/**
 * Provider model lists
 */
export interface ProviderModels {
  text?: string[];
  image?: string[];
  defaults?: {
    text?: string;
    image?: string;
  };
}

/**
 * Complete provider configuration
 */
export interface ProviderConfig {
  id: ProviderId;
  label: string;
  capabilities: ProviderCapabilities;
  validation: ProviderValidation;
  display: ProviderDisplay;
  imageModels?: ProviderImageModels;
  models?: ProviderModels;
  documentSupport?: ProviderDocumentSupport;
  imageDetails?: ProviderImageDetails;
  implementation: AIProvider;
}

export class ProviderRegistry {
  private providers = new Map<ProviderId, ProviderConfig>();

  register(config: ProviderConfig): void {
    if (this.providers.has(config.id)) {
      console.warn(`Provider ${config.id} is already registered. Overwriting.`);
    }
    this.providers.set(config.id, config);
  }

  get(providerId: ProviderId): ProviderConfig | null {
    return this.providers.get(providerId) || null;
  }

  getAll(): ProviderConfig[] {
    return Array.from(this.providers.values());
  }

  has(providerId: ProviderId): boolean {
    return this.providers.has(providerId);
  }

  getByCapability(capability: keyof ProviderCapabilities): ProviderConfig[] {
    return this.getAll().filter(
      (provider) => provider.capabilities[capability] === true
    );
  }

  count(): number {
    return this.providers.size;
  }
}

/**
 * Global provider registry instance
 *
 * This is the main registry used throughout the application.
 * Provider implementations should register themselves with this instance.
 */
export const providerRegistry = new ProviderRegistry();
